import React from 'react';
import { Badge } from '../Badge';
type Step = {
  number: string;
  title: string;
  duration: string;
  description: string;
};
const step1: Step = {
  number: '1',
  title: 'Onboarding Call',
  duration: '1 hour',
  description: 'Walk through your tech stack, lead sources, and qualification flow so we know exactly what your best rep would say.'
};
const step2a: Step = {
  number: '2a',
  title: 'Custom AI Training',
  duration: '1–3 days',
  description: 'We train the model on your real or synthetic customer conversations so it sounds like you, not a script.'
};
const step2b: Step = {
  number: '2b',
  title: 'Custom Integrations',
  duration: '1–3 days',
  description: 'We connect your CRM, scheduling, and lead sources so the AI books, updates, and hands off cleanly.'
};
const step3: Step = {
  number: '3',
  title: 'Run',
  duration: 'Day 4 onward',
  description: 'Your AI rep is live — answering, qualifying, and booking every inbound lead 24/7. You watch the pipeline fill.'
};
function StepCard({
  step
}: {
  step: Step;
}) {
  return <div className="border border-gray-300 rounded-2xl bg-white p-6 flex flex-col gap-3 h-full" data-id="element-123">
      <div className="flex items-center gap-3" data-id="element-124">
        <div className="w-9 h-9 rounded-full border border-navy-500 bg-navy-500 text-white flex items-center justify-center text-sm font-bold" data-id="element-125">
          {step.number}
        </div>
        <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-gray-500" data-id="element-126">
          {step.duration}
        </div>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 leading-tight" data-id="element-127">
        {step.title}
      </h3>
      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-128">
        {step.description}
      </p>
    </div>;
}
function StepArrow() {
  return <div className="hidden lg:flex items-center justify-center text-gray-400" aria-hidden data-id="element-129">
      <span className="text-2xl" data-id="element-130">→</span>
    </div>;
}
export function HowItWorks() {
  return <section id="how" className="w-full border-b border-gray-200 bg-gray-50 px-4 sm:px-6 lg:px-12 py-12 sm:py-20 lg:py-24 scroll-mt-20" data-id="element-131">
      <div className="mx-auto max-w-[1280px]" data-id="element-132">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 text-center max-w-2xl mx-auto mb-10 sm:mb-14" data-id="element-133">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-navy-500 text-white uppercase tracking-[0.15em] text-[11px] font-semibold" data-id="element-134">
            How It Works
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-gray-900 tracking-tight leading-[1.1]" data-id="element-135">
            Live in under a week.
          </h2>
          <p className="text-base text-gray-600 leading-relaxed" data-id="element-136">
            One short call, a few days of setup on our side, and you're done. No
            staff to hire. No platform to learn.
          </p>
        </div>

        {/* Timeline — desktop: 3 columns with arrows; mobile: stacked */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1.2fr_auto_1fr] gap-4 lg:gap-6 items-stretch" data-id="element-137">
          {/* Step 1 */}
          <StepCard step={step1} data-id="element-138" />

          <StepArrow data-id="element-139" />

          {/* Step 2 — parallel pair */}
          <div className="relative border border-dashed border-gray-400 rounded-2xl p-4 bg-white/50 flex flex-col gap-3" data-id="element-140">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-50 px-3" data-id="element-141">
              <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-600" data-id="element-142">
                Run in parallel
              </span>
            </div>
            <StepCard step={step2a} data-id="element-143" />
            <StepCard step={step2b} data-id="element-144" />
          </div>

          <StepArrow data-id="element-145" />

          {/* Step 3 */}
          <StepCard step={step3} data-id="element-146" />
        </div>

        {/* Footer summary */}
        <div className="mt-10 sm:mt-12 flex flex-col items-center gap-2 text-center" data-id="element-147">
          <div className="inline-flex items-center gap-3 text-sm text-gray-700" data-id="element-148">
            <span className="font-semibold text-gray-900" data-id="element-149">
              Total time to launch:
            </span>
            <span className="font-mono text-gray-900" data-id="element-150">~4 days</span>
          </div>
          <p className="text-xs text-gray-500" data-id="element-151">
            No engineering work required on your end.
          </p>
        </div>
      </div>
    </section>;
}